import { useCallback, useEffect, useRef, useState } from 'react';
import { type Room, RoomEvent, type RemoteParticipant } from 'livekit-client';
import type { CallExtendSignal } from '@aslgo/shared';

const APPROVAL_TIMEOUT_MS = 20_000;

/**
 * Guide-approval round-trip for extending an in-call session, sent over the
 * call room's data channel (mirrors useCallChat's transport). The learner's
 * `requestExtend` publishes an `extend-request` and waits up to 20s for a
 * response; the guide sees `pendingRequest` and calls `respond`. On accept
 * both sides fire `onApproved(minutes)` so their local countdown timers stay
 * in sync — the learner via the `extend-accept` handler, the guide via
 * `respond(true)` directly.
 */
export function useCallExtend({ room, userId, isLearner, onApproved }: {
  /** Local user's auth uuid; rides along as the request's `from`. */
  room: Room | null; userId: string | null; isLearner: boolean; onApproved: (minutes: number) => void;
}) {
  const [pendingRequest, setPendingRequest] = useState<{ from: string; minutes: number } | null>(null);
  const [awaiting, setAwaiting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onApprovedRef = useRef(onApproved); useEffect(() => { onApprovedRef.current = onApproved; });
  const awaitingRef = useRef(awaiting); useEffect(() => { awaitingRef.current = awaiting; }, [awaiting]);

  useEffect(() => {
    if (!room) return;
    const handler = (payload: Uint8Array, _p?: RemoteParticipant) => {
      let m: CallExtendSignal;
      try { m = JSON.parse(new TextDecoder().decode(payload)); } catch { return; }
      if (m.type === 'extend-request' && !isLearner) {
        setPendingRequest({ from: m.from, minutes: m.minutes });
        if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = setTimeout(() => setPendingRequest(null), APPROVAL_TIMEOUT_MS);
      } else if (m.type === 'extend-accept' && isLearner) {
        // A late accept after the learner's own 20s timeout is a no-op — the
        // learner already gave up, so don't resurrect the countdown extend.
        if (!awaitingRef.current) return;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setAwaiting(false); onApprovedRef.current(m.minutes);
      } else if (m.type === 'extend-decline' && isLearner) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setAwaiting(false);
      }
    };
    room.on(RoomEvent.DataReceived, handler);
    return () => { room.off(RoomEvent.DataReceived, handler); };
  }, [room, isLearner]);

  // Clear the guide's auto-dismiss timer on unmount.
  useEffect(() => () => { if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current); }, []);

  const publish = useCallback((sig: CallExtendSignal) => {
    if (!room) return;
    void room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(sig)), { reliable: true });
  }, [room]);

  const requestExtend = useCallback((minutes: number) => {
    if (!userId) return;
    setAwaiting(true);
    publish({ type: 'extend-request', from: userId, minutes });
    timeoutRef.current = setTimeout(() => setAwaiting(false), APPROVAL_TIMEOUT_MS);
  }, [userId, publish]);

  const respond = useCallback((accept: boolean) => {
    if (pendingTimeoutRef.current) { clearTimeout(pendingTimeoutRef.current); pendingTimeoutRef.current = null; }
    const req = pendingRequest;
    setPendingRequest(null);
    if (!req) return;
    if (accept) { publish({ type: 'extend-accept', minutes: req.minutes }); onApprovedRef.current(req.minutes); }
    else publish({ type: 'extend-decline' });
  }, [pendingRequest, publish]);

  return { pendingRequest, awaiting, requestExtend, respond };
}
